import {
  articles,
  serviceCategories,
  industries,
  navLinks,
} from "@/lib/content";
import { defaultFirmContact, type FirmContact } from "@/lib/firm-contact";
import type { Leader } from "@/lib/types/content";
import type { ServiceCategory } from "@/lib/content";

export type Hit = {
  title: string;
  group: string;
  description?: string;
  to: string;
  params?: Record<string, string>;
  keywords: string;
};

export type SearchIndexInput = {
  services?: ServiceCategory[];
  insights?: Array<{ slug: string; title: string; excerpt: string; category: string }>;
  stories?: Array<{
    slug: string;
    title: string;
    excerpt: string;
    client?: string;
    industry?: string;
    service?: string;
  }>;
  leaders?: Leader[];
  careers?: Array<{
    slug: string;
    title: string;
    excerpt?: string;
    department?: string;
    location?: string;
  }>;
  firmContact?: FirmContact;
};

export function buildSearchIndex(data: SearchIndexInput = {}): Hit[] {
  const items: Hit[] = [];
  const services = data.services?.length ? data.services : serviceCategories;
  const insights = data.insights?.length ? data.insights : articles;
  const stories = data.stories ?? [];
  const leaderList = data.leaders ?? [];
  const careers = data.careers ?? [];
  const contact = data.firmContact ?? defaultFirmContact;

  for (const n of navLinks) {
    items.push({
      title: n.label,
      group: "Pages",
      to: n.to,
      keywords: `${n.label} ${n.to}`,
    });
  }

  for (const s of services) {
    items.push({
      title: s.name,
      group: "Services",
      description: s.short,
      to: "/services/$slug",
      params: { slug: s.slug },
      keywords: `${s.name} ${s.short} ${s.intro}`,
    });
    for (const item of s.items) {
      items.push({
        title: item.name,
        group: s.name,
        description: item.description,
        to: "/services/$slug",
        params: { slug: s.slug },
        keywords: `${item.name} ${item.description} ${s.name}`,
      });
    }
  }

  for (const a of insights) {
    items.push({
      title: a.title,
      group: "Insights",
      description: a.excerpt,
      to: "/insights/$slug",
      params: { slug: a.slug },
      keywords: `${a.title} ${a.excerpt} ${a.category}`,
    });
  }

  for (const story of stories) {
    items.push({
      title: story.title,
      group: "Client Stories",
      description: story.excerpt,
      to: "/client-stories/$slug",
      params: { slug: story.slug },
      keywords: `${story.title} ${story.excerpt} ${story.client ?? ""} ${story.industry ?? ""} ${story.service ?? ""}`,
    });
  }

  for (const l of leaderList) {
    items.push({
      title: l.name,
      group: "Leadership",
      description: `${l.designation} · ${l.qualifications}`,
      to: "/leadership/$slug",
      params: { slug: l.slug },
      keywords: `${l.name} ${l.designation} ${l.qualifications} ${l.memberships.join(" ")}`,
    });
  }

  for (const job of careers) {
    items.push({
      title: job.title,
      group: "Careers",
      description: [job.department, job.location].filter(Boolean).join(" · ") || job.excerpt,
      to: "/careers",
      keywords: `${job.title} ${job.excerpt ?? ""} ${job.department ?? ""} ${job.location ?? ""} careers jobs`,
    });
  }

  for (const ind of industries) {
    items.push({
      title: ind.name,
      group: "Industries",
      description: ind.body,
      to: "/industries",
      keywords: `${ind.name} ${ind.body}`,
    });
  }

  items.push({
    title: "Contact us",
    group: "Pages",
    description: `${contact.phone} · ${contact.email}`,
    to: "/contact",
    keywords: `contact email phone enquiry ${contact.email} ${contact.phone}`,
  });

  return items;
}

export function searchHits(index: Hit[], query: string, limit = 12): Hit[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const tokens = q.split(/\s+/).filter(Boolean);
  return index
    .map((h) => {
      const hay = `${h.title} ${h.description ?? ""} ${h.keywords}`.toLowerCase();
      let score = 0;
      let matchedTokens = 0;
      for (const t of tokens) {
        const i = hay.indexOf(t);
        if (i === -1) continue;
        matchedTokens += 1;
        if (i === 0) score += 5;
        else if (h.title.toLowerCase().includes(t)) score += 3;
        else score += 1;
      }
      if (matchedTokens === 0) return null;
      if (matchedTokens === tokens.length) score += tokens.length * 2;
      return { hit: h, score };
    })
    .filter((x): x is { hit: Hit; score: number } => x !== null)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((x) => x.hit);
}
